import React, {useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {FormApi} from 'final-form';

import {createPatch, fetchResource, patchResource, resetResource} from 'spectra-logic-ui/actions';
import {WizardFormDialog} from 'spectra-logic-ui/components';
import {Dispatch, Store} from 'spectra-logic-ui';
import {numberToHumanSize} from 'spectra-logic-ui/helpers/number';

import {FormActionType, TargetType} from '@/enum';
import {CloudProperties, VolumeProperties, Pool, PoolPatchFields, TargetItemDetails} from '@/types';
import {EditOption} from '@/storage/form/enum';
import {FormValues} from '@/storage/form/types';
import OptionsPage from '@/storage/form/edit/options';
import {validateEditAuthorization, validateEditParameters} from '@/storage/form/pages/validate';
import OnSiteConfirmPage from '@/storage/form/pages/onsite/confirm';
import OnSiteParametersPage from '@/storage/form/pages/onsite/parameters';
import CloudAuthorizationPage from '@/storage/form/pages/cloud/authorization';
import CloudConfirmPage from '@/storage/form/pages/cloud/confirm';
import CloudParametersPage from '@/storage/form/pages/cloud/parameters';
import {isCloud, isCloudStorage} from '@/storage/cloud';
import {itemDetails} from '@/storage/form/store';

type Props = {
  pool: Pool;
  itemDetails: TargetItemDetails;
  fetchItemDetails: Function;
  resetItemDetails: Function;
  onSubmit: (values: FormValues) => Promise<any>;
}

const EditStorageWizard = ({pool, itemDetails, fetchItemDetails, resetItemDetails, onSubmit, ...others}: Props) => {
  const initialValues: FormValues = {
    editOption: EditOption.CHANGE_PARAMS, ...pool,
  } as any;

  const title = pool !== undefined && pool.endpoint === '0' ? 'Edit Cloud Storage' : 'Edit Endpoint Storage';

  const [values, setValues] = useState({} as any);
  const endpoint = values.endpoint;
  const selectedOption = values['editOption'];

  // We need to fetch item details for all storage types so we can show the storage
  // class options. We also need it specifically for S3OTHER so we can pre-populate
  // the endpoint field.
  useEffect(() => {
    fetchItemDetails();
    return () => resetItemDetails();
  }, []);

  const formRef: React.MutableRefObject<FormApi> = useRef({} as any);
  useEffect(() => {
    if (isCloud(pool)) {
      const cloudProperties = itemDetails.properties as CloudProperties;
      if (formRef && formRef.current && formRef.current.mutators && cloudProperties) {
        formRef.current.mutators.setFormValue('theirClass', cloudProperties.theirClass);
        if (pool.target === TargetType.S3OTHER) {
          if (cloudProperties.endpoint) {
            formRef.current.mutators.setFormValue('url', cloudProperties.endpoint);
          }
          formRef.current.mutators.setFormValue('unsafe', cloudProperties.unsafe);
          // Radio buttons are used instead of a single checkbox for virtualHost.
          formRef.current.mutators.setFormValue('virtualHost', cloudProperties.virtualHost ? 'true' : 'false');
        }
      }
    }
    if (pool.target === TargetType.VOLUME) {
      const volumeProperties = itemDetails.properties as VolumeProperties;
      if (formRef && formRef.current && formRef.current.mutators && volumeProperties) {
        const volQuota = volumeProperties.quota;
        const humanSize = numberToHumanSize(volQuota, {commafy: false, separator: ';'});
        const units = humanSize.split(';')[1];
        const quota = humanSize.split(';')[0];
        formRef.current.mutators.setFormValue('quota', quota);
        formRef.current.mutators.setFormValue('units', units);
      }
    }
  }, [itemDetails]);

  return (
    <WizardFormDialog
      title={title} initialValues={initialValues} onSubmit={onSubmit} onValuesChange={setValues}
      formRef={formRef} {...others}
    >
      {!isCloud(pool) &&
        <React.Fragment>
          <OnSiteParametersPage title='Parameters' action={FormActionType.EDIT}
            pool={pool} endpoint={endpoint} validate={validateEditParameters} />
          <OnSiteConfirmPage title='Confirm' />
        </React.Fragment>
      }
      {isCloud(pool) &&
        <React.Fragment>
          <OptionsPage title='Options' pool={pool} />
          {selectedOption === EditOption.CHANGE_PARAMS &&
            <CloudParametersPage title='Parameters' action={FormActionType.EDIT} pool={pool}
              validate={validateEditParameters} />}
          {selectedOption === EditOption.CHANGE_AUTH &&
            <CloudAuthorizationPage title='Authorization' action={FormActionType.EDIT}
              validate={validateEditAuthorization} />}
          <CloudConfirmPage title='Confirm' action={FormActionType.EDIT} />
        </React.Fragment>
      }
    </WizardFormDialog>
  );
};

const mapStateToProps = (state: Store) => {
  const itemState = state.resources[itemDetails] || {};
  return {itemDetails: itemState.data || {}};
};

const mapDispatchToProps = (dispatch: Dispatch, {pool}: Props) => ({
  fetchItemDetails: () => {
    const opts = {storeName: itemDetails};
    return dispatch(fetchResource('storage/' + pool.id + '/item', '', opts));
  },
  resetItemDetails: () => dispatch(resetResource(itemDetails)),
  onSubmit: (values: any) => {
    const {...props} = values;
    let body: any = {};
    if (props.editOption === EditOption.CHANGE_PARAMS) {
      delete props.accessKey;
      delete props.secretKey;
      delete props.arn;
      delete props.externalid;
      delete props.url;
      delete props.unsafe;
      delete props.region;
      delete props.credentials;
      delete props.account;
      delete props.secret;

      body = createPatch(PoolPatchFields, pool, props);
      if (isCloudStorage(props.target)) {
        // Just always send down the parameters from item-details since it's easier than trying to figure out
        // if the user actually changed them.
        body.parameters = {theirClass: props.theirClass ? props.theirClass : null};
        delete props.theirClass;
        if (props.target === TargetType.S3OTHER) {
          body.parameters.virtualHost = props.virtualHost === 'true';
        }
      }
      if (props.target === TargetType.VOLUME) {
        const unitToBytes: {[key: string]: number} = {
          'MiB': 1024 * 1024,
          'GiB': 1024 * 1024 * 1024,
          'TiB': 1024 * 1024 * 1024 * 1024,
          'PiB': 1024 * 1024 * 1024 * 1024 * 1024,
        };
        if (props.quota === null || props.quota == undefined || props.quota === '') {
          body.parameters = {quota: ''};
        } else {
          const units: string = props.units;
          const rawQuota = unitToBytes[units] * props.quota;
          body.parameters = {quota: rawQuota.toString()};
        }
        delete props.units;
        delete props.quota;
      }
    } else {
      // Changing authorization basically means forcing the user to re-enter all
      // the authorization parameters as opposed to displaying the existing ones
      // and only changing certain ones. Thus, we just always send down the applicable
      // params instead of using `createPatch`.
      const params: any = {};
      if (props.target === TargetType.AZURE) {
        params.secret = props.secret;
      } else if (props.target === TargetType.GOOGLE) {
        params.credentials = props.credentials;
      } else {
        if (props.target === TargetType.S3OTHER) {
          params.endpoint = props.url;
          params.unsafe = props.unsafe;
        }
        params.region = props.region || '';
        params.accessKey = props.accessKey || '';
        params.secretKey = props.secretKey || '';
        params.arn = props.arn || '';
        params.externalid = props.externalid || '';
      }
      body.parameters = params;
    }
    delete body.id;
    delete body.editOption;
    if (!pool.importer) {
      delete body.pauseNotifications;
    }
    return dispatch(patchResource('storage', values.id, body));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(EditStorageWizard);
